import { ChangeDetectionStrategy, Component, computed, effect, inject } from '@angular/core'
import { CommonModule } from '@angular/common'
import { RouterLink } from '@angular/router'
import { injectParams } from 'ngxtension/inject-params'
import { injectQuery } from '@tanstack/angular-query-experimental'
import { lastValueFrom } from 'rxjs'
import { Skeleton } from '@openng/optimus-ui/skeleton'

import { SellerProfileService, SellerBadgeType, sellerBadgeMeta } from '../../../core/seller-profile.service'
import { SeoService } from '../../../core/seo.service'
import { MarketplaceReviewsSection } from '../../../shared/marketplace-reviews/marketplace-reviews-section'
import { faNum } from '../equipment-page/equipment-labels'

const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i

/**
 * Marketplace M7/M8 — public seller profile (/sellers/:kcUserId):
 * verification badges with Persian labels, the BeautyBoss Trust Score with its
 * confidence («دقت امتیاز»), the star rating summary and the seller's review
 * list. Public GETs only — safe for guests and SSR
 * (app.routes.server.ts renders this route on the server for SEO).
 */
@Component({
	selector: 'app-seller-profile-page',
	standalone: true,
	imports: [CommonModule, RouterLink, Skeleton, MarketplaceReviewsSection],
	changeDetection: ChangeDetectionStrategy.OnPush,
	template: `
		<div dir="rtl" class="min-h-screen bg-slate-50">
			<div class="max-w-4xl mx-auto px-4 py-8 md:py-10">
				<a routerLink="/" class="text-xs text-slate-400 hover:text-purple-600 inline-flex items-center gap-1 mb-4">
					<i class="pi pi-arrow-right text-[10px]"></i> بازگشت به بیوتی‌باس
				</a>

				@if (query.isPending()) {
					<div class="bg-white rounded-3xl border border-slate-100 p-8 shadow-sm">
						<p-skeleton height="3rem" width="50%" styleClass="rounded-xl mb-4" />
						<p-skeleton height="1.5rem" width="70%" class="mb-3" />
						<p-skeleton height="1.5rem" width="40%" />
					</div>
				} @else if (query.isError() || !profile(); as p) {
					<div class="bg-white rounded-3xl border border-slate-100 p-12 text-center shadow-sm">
						<i class="pi pi-user text-4xl text-slate-300 mb-4 block"></i>
						<h1 class="text-xl font-black text-slate-700 mb-2">فروشنده یافت نشد</h1>
						<p class="text-sm text-slate-500 mb-6">ممکن است این پروفایل در دسترس نیست یا آدرس اشتباه است.</p>
						<a
							routerLink="/equipment"
							class="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-purple-600 text-white text-sm font-bold"
							>بازگشت به تجهیزات</a
						>
					</div>
				} @else {
					<!-- Header: title + summary stars -->
					<div class="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden mb-6">
						<div class="h-20 md:h-24 bg-gradient-to-l from-purple-500 via-purple-600 to-pink-500"></div>
						<div class="px-6 md:px-8 pb-7">
							<div class="flex items-center gap-4 -mt-9">
								<div
									class="w-18 h-18 md:w-20 md:h-20 rounded-3xl border-4 border-white shadow bg-purple-100 flex items-center justify-center shrink-0">
									<i class="pi pi-user text-2xl text-purple-400"></i>
								</div>
								<div class="pt-5">
									<h1 class="text-lg md:text-2xl font-black text-slate-800">فروشنده بیوتی‌باس</h1>
									<p class="text-[11px] text-slate-400 mt-0.5" dir="ltr">شناسه: {{ kcUserId() }}</p>
								</div>
							</div>

							<!-- Rating summary (stars) -->
							@if (summary(); as s) {
								<div class="flex items-center gap-3 flex-wrap text-xs mt-5">
									<span class="inline-flex items-center gap-1.5" dir="ltr">
										<span class="text-amber-400 text-base">{{ stars(s.avgSellerRating) }}</span>
										<span class="text-slate-200 text-base">{{ stars(5 - Math.round(s.avgSellerRating ?? 0)) }}</span>
									</span>
									<b class="text-slate-700 text-sm">{{ faNum(s.avgSellerRating) }}</b>
									<span class="text-slate-400">
										امتیاز فروشنده ({{ (s.sellerCount ?? 0).toLocaleString('fa-IR') }} نظر)
										@if (s.itemCount) {
											— امتیاز کالا {{ faNum(s.avgItemRating) }} ({{ (s.itemCount ?? 0).toLocaleString('fa-IR') }} نظر)
										}
									</span>
									@if (showSatisfaction()) {
										<span
											class="inline-flex items-center gap-1 bg-emerald-50 text-emerald-600 font-bold px-2.5 py-1 rounded-lg">
											<i class="pi pi-face-smile"></i> رضایت {{ faNum(s.satisfactionPercent, 0) }}٪ کاربران
										</span>
									} @else {
										<span
											class="inline-flex items-center gap-1 bg-slate-50 text-slate-400 font-bold px-2.5 py-1 rounded-lg">
											<i class="pi pi-info-circle"></i> داده کافی وجود ندارد
										</span>
									}
								</div>
							}

							<!-- Verification badges -->
							@if (badges().length) {
								<div class="flex flex-wrap gap-2 mt-4">
									@for (b of badges(); track b) {
										@if (badgeMeta(b); as meta) {
											<span
												[ngClass]="badgeClass(meta.severity)"
												class="inline-flex items-center gap-1.5 text-[11px] md:text-xs font-bold px-3 py-1.5 rounded-xl border">
												<i [class]="meta.icon"></i> {{ meta.label }}
											</span>
										}
									}
								</div>
							} @else {
								<p class="text-[11px] text-slate-400 mt-4">هنوز بج تأییدی برای این فروشنده ثبت نشده است.</p>
							}
						</div>
					</div>

					<!-- Trust score -->
					@if (trust(); as t) {
						<div class="bg-white rounded-3xl border border-slate-100 p-6 md:p-8 shadow-sm mb-6">
							<div class="flex items-center justify-between gap-4 flex-wrap">
								<h2 class="text-base font-black text-slate-800 flex items-center gap-2">
									<i class="pi pi-shield text-purple-500"></i> اعتمادسنجی بیوتی‌باس
								</h2>
								<div class="text-left">
									<p class="text-3xl font-black text-purple-600">{{ faNum(t.score, 0) }}</p>
									<p class="text-[10px] text-slate-400">امتیاز اعتماد از ۱۰۰</p>
								</div>
							</div>
							@if (hasConfidence()) {
								<div class="mt-4">
									<div class="flex justify-between text-[11px] text-slate-500 mb-1">
										<span>دقت امتیاز</span>
										<b>{{ faNum(confidencePercent(), 0) }}٪</b>
									</div>
									<div class="h-2 bg-slate-100 rounded-full overflow-hidden">
										<div
											[style.width.%]="confidencePercent()"
											class="h-full bg-gradient-to-l from-purple-500 to-pink-400 rounded-full"></div>
									</div>
									<p class="text-[10px] text-slate-400 mt-1.5 leading-5"
										>هرچه داده‌های بیشتری از معامله‌های فروشنده موجود باشد، این دقت بالاتر است.</p
									>
								</div>
							}
						</div>
					}

					<!-- Reviews (reused shared block — profile link hidden here) -->
					@if (kcUserId(); as sid) {
						<app-marketplace-reviews-section [sellerKcUserId]="sid" [showProfileLink]="false" />
					}
				}
			</div>
		</div>
	`,
})
export default class SellerProfilePage {
	private readonly profileService = inject(SellerProfileService)
	private readonly seo = inject(SeoService)

	readonly Math = Math
	readonly faNum = faNum
	readonly badgeMeta = sellerBadgeMeta

	readonly kcUserIdParam = injectParams('kcUserId')

	readonly kcUserId = computed(() => {
		const raw = this.kcUserIdParam()
		// Only a full UUID is a valid profile key (mis-typed slugs fall through to not-found).
		return raw?.match(UUID_RE)?.[0] ?? raw ?? null
	})

	readonly query = injectQuery(() => ({
		queryKey: ['seller-profile', this.kcUserId()],
		enabled: !!this.kcUserId(),
		queryFn: () => lastValueFrom(this.profileService.getProfile(this.kcUserId() ?? '')),
	}))

	readonly profile = computed(() => this.query.data() ?? null)
	readonly badges = computed<SellerBadgeType[]>(() => this.profile()?.badges ?? [])
	readonly summary = computed(() => this.profile()?.ratingSummary ?? null)
	readonly trust = computed(() => this.profile()?.trustScore ?? null)

	/** «دقت امتیاز» bar only with a real confidence value (TS-side null check). */
	readonly hasConfidence = computed(() => this.trust()?.confidence != null)

	/** confidence clamped to 0..100 for the bar + label. */
	readonly confidencePercent = computed(() => {
		const c = this.trust()?.confidence
		return c == null ? 0 : Math.min(100, Math.max(0, c))
	})

	/** Satisfaction chip only with real data (TS-side null check). */
	readonly showSatisfaction = computed(() => {
		const s = this.summary()
		return !!s?.enoughData && s.satisfactionPercent != null
	})

	badgeClass(severity: 'success' | 'info' | 'warn' | 'secondary'): string {
		switch (severity) {
			case 'success':
				return 'bg-emerald-50 text-emerald-600 border-emerald-200'
			case 'info':
				return 'bg-sky-50 text-sky-600 border-sky-200'
			case 'warn':
				return 'bg-amber-50 text-amber-600 border-amber-200'
			default:
				return 'bg-slate-50 text-slate-500 border-slate-200'
		}
	}

	stars(value?: number | null): string {
		return '★'.repeat(Math.max(0, Math.min(5, Math.round(value ?? 0))))
	}

	constructor() {
		effect(() => {
			const id = this.kcUserId()
			if (!id) return
			this.seo.setSeo({
				title: 'پروفایل فروشنده | بازار تجهیزات بیوتی‌باس',
				description: 'بج‌های تأیید، اعتمادسنجی و نظرات خریداران تأییدشده این فروشنده در بیوتی‌باس.',
				keywords: 'فروشنده, پروفایل فروشنده, نظرات, اعتمادسنجی, بیوتی‌باس',
				canonicalPath: `/sellers/${id}`,
			})
		})
		effect(() => {
			if (this.query.isError()) this.seo.setNotFound()
		})
	}
}
